import * as Sentry from '@sentry/react';
import * as React from 'react';

import { useAdminCurrentUser, type AdminCurrentUser } from '../hooks/queries/useAdminCurrentUser';
import { getGoogleStartUrl } from '../lib/mopsAuthUrls';

interface IAuthContextValue {
  user: AdminCurrentUser | null;
  loading: boolean;
}

const defaultValues: IAuthContextValue = {
  user: null,
  loading: true,
};

const AuthContext = React.createContext<IAuthContextValue>(defaultValues);

type IAuthProviderProps = React.PropsWithChildren;

/**
 * Validates the mops-scoped session on mount by running the `adminCurrentUser`
 * query. On failure (no cookie, expired session, non-admin email), `ApolloProvider`'s
 * error link redirects to `/auth/google/start`; if the query succeeds with a null
 * user (shouldn't happen — the resolver throws) we redirect here too.
 *
 * The provider blocks rendering children until the initial session check resolves,
 * so downstream consumers can rely on `user` being non-null without optional chaining.
 */
const AuthProvider = ({ children }: IAuthProviderProps) => {
  const { user, loading, error, refetch } = useAdminCurrentUser();

  React.useEffect(() => {
    const handleWindowFocus = () => {
      void refetch();
    };
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [refetch]);

  React.useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Belt-and-suspenders redirect if the query finished without an error or a user.
  // (`ApolloProvider`'s error link handles the 401/403 path, but a `null` data
  // value with no error shouldn't leave the operator staring at a blank screen.)
  React.useEffect(() => {
    if (!loading && !user && !error) {
      window.location.assign(getGoogleStartUrl());
    }
  }, [loading, user, error]);

  if (loading || !user) {
    return null;
  }

  const contextValue: IAuthContextValue = {
    user,
    loading,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuthState = () => React.useContext(AuthContext);

export default AuthProvider;
